import { Box, Text, UnstyledButton } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import {
	AnalyticsEvent,
	AnalyticsEventType,
	AnalyticsJourney,
	AnalyticsStage,
	analyticsSdk
} from '@newo/analytics';
import { PhoneController, type PhoneControllerFieldProps } from '@newo/ui';
import { X } from 'lucide-react';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import PhoneInput from '@/components/PhoneInput';
import { useHostContext } from '@/context/HostContext.tsx';
import {
	type StepperOnboardingFormValues,
	StepperSetupStepKey
} from '@/stepper/types.ts';
import { STEP_CONTENT_CLASS_NAMES } from '../../classNames';
import StepperNextButton from '../StepperNextButton';
import styles from './StepperTransfersStepContent.module.css';
import StepperTransfersTextField from './StepperTransfersTextField';

interface StepperTransfersStepContentProps {
	isLocked?: boolean;
	isPending: boolean;
	onNext: () => void;
}

const StepperTransfersStepContent: FC<StepperTransfersStepContentProps> = ({
	isLocked = false,
	isPending,
	onNext
}) => {
	const { localization } = useHostContext();
	const { clearErrors, control, setError, setValue } =
		useFormContext<StepperOnboardingFormValues>();
	const [phoneInputVersion, setPhoneInputVersion] = useState(0);
	const [managerEmail, managerFullName, managerPhone] = useWatch({
		control,
		name: ['managerEmail', 'managerFullName', 'managerPhone']
	});
	const previousManagerPhoneRef = useRef(managerPhone);
	const phoneFieldOnChangeRef = useRef<
		PhoneControllerFieldProps['onChange'] | null
	>(null);
	const hasManagerPhone = Boolean(managerPhone?.trim());

	const debouncedManagerPhoneChange = useDebouncedCallback(
		(nextManagerPhone: string) => {
			if (!nextManagerPhone.trim()) {
				return;
			}

			analyticsSdk.track(AnalyticsEvent.FieldChanged, {
				field: 'managerPhone',
				journey: AnalyticsJourney.CreatorSetup,
				manager_phone: nextManagerPhone.trim(),
				stage: AnalyticsStage.CreatorOnboarding,
				step: StepperSetupStepKey.CallSummariesTransfers,
				type: AnalyticsEventType.Field
			});
		},
		600
	);

	const handleManagerPhoneChange = useCallback(
		(nextManagerPhone: string) => {
			const previousManagerPhone = previousManagerPhoneRef.current.trim();
			previousManagerPhoneRef.current = nextManagerPhone;

			if (!nextManagerPhone.trim() && previousManagerPhone) {
				analyticsSdk.track(AnalyticsEvent.FieldCleared, {
					field: 'managerPhone',
					journey: AnalyticsJourney.CreatorSetup,
					previous_manager_phone: previousManagerPhone,
					stage: AnalyticsStage.CreatorOnboarding,
					step: StepperSetupStepKey.CallSummariesTransfers,
					type: AnalyticsEventType.Field
				});
			}

			debouncedManagerPhoneChange(nextManagerPhone);
		},
		[debouncedManagerPhoneChange]
	);

	const handleManagerPhoneFocus = useCallback(() => {
		analyticsSdk.track(AnalyticsEvent.FieldFocused, {
			field: 'managerPhone',
			journey: AnalyticsJourney.CreatorSetup,
			stage: AnalyticsStage.CreatorOnboarding,
			step: StepperSetupStepKey.CallSummariesTransfers,
			type: AnalyticsEventType.Field
		});
	}, []);

	const handlePhoneInputChange = useCallback(
		(value: string) => {
			handleManagerPhoneChange(value);
			phoneFieldOnChangeRef.current?.(value);
		},
		[handleManagerPhoneChange]
	);

	const phoneInputProps = useMemo(
		() => ({
			'data-testid': 'stepper-manager-phone-input',
			disabled: isLocked,
			onFocus: handleManagerPhoneFocus,
			placeholder: localization.translate(
				'creator',
				'stepperOnboarding.steps.callSummariesTransfers.managerPhonePlaceholder'
			),
			readOnly: isLocked
		}),
		[handleManagerPhoneFocus, isLocked, localization]
	);

	const handleClearPhone = useCallback(() => {
		if (isLocked) {
			return;
		}

		handleManagerPhoneChange('');
		phoneFieldOnChangeRef.current?.('');
		setValue('managerPhone', '', {
			shouldDirty: true,
			shouldTouch: true,
			shouldValidate: true
		});
		setPhoneInputVersion((version) => version + 1);
	}, [handleManagerPhoneChange, isLocked, setValue]);

	const handleNextClick = useCallback(() => {
		analyticsSdk.track(AnalyticsEvent.ButtonClicked, {
			action: 'next',
			journey: AnalyticsJourney.CreatorSetup,
			manager_email: managerEmail.trim(),
			manager_full_name: managerFullName.trim(),
			manager_phone: managerPhone.trim(),
			stage: AnalyticsStage.CreatorOnboarding,
			step: StepperSetupStepKey.CallSummariesTransfers,
			type: AnalyticsEventType.Interaction
		});
		onNext();
	}, [managerEmail, managerFullName, managerPhone, onNext]);

	return (
		<Box className={STEP_CONTENT_CLASS_NAMES.stepBody}>
			<Text className={STEP_CONTENT_CLASS_NAMES.stepSubtitle}>
				{localization.translate(
					'creator',
					'stepperOnboarding.steps.callSummariesTransfers.description'
				)}
			</Text>

			<Box className={STEP_CONTENT_CLASS_NAMES.formStack}>
				<StepperTransfersTextField
					clearLabel={localization.translate(
						'creator',
						'stepperOnboarding.steps.callSummariesTransfers.clearManagerEmail'
					)}
					isLocked={isLocked}
					label={localization.translate(
						'creator',
						'stepperOnboarding.steps.callSummariesTransfers.managerEmailLabel'
					)}
					name="managerEmail"
					placeholder={localization.translate(
						'creator',
						'stepperOnboarding.steps.callSummariesTransfers.managerEmailPlaceholder'
					)}
				/>

				<StepperTransfersTextField
					clearLabel={localization.translate(
						'creator',
						'stepperOnboarding.steps.callSummariesTransfers.clearManagerFullName'
					)}
					isLocked={isLocked}
					label={localization.translate(
						'creator',
						'stepperOnboarding.steps.callSummariesTransfers.managerFullNameLabel'
					)}
					name="managerFullName"
					placeholder={localization.translate(
						'creator',
						'stepperOnboarding.steps.callSummariesTransfers.managerFullNamePlaceholder'
					)}
				/>

				<Box>
					<Text
						className={STEP_CONTENT_CLASS_NAMES.formLabel}
						component="label"
					>
						{localization.translate(
							'creator',
							'stepperOnboarding.steps.callSummariesTransfers.managerPhoneLabel'
						)}
					</Text>
					<PhoneController
						clearErrors={clearErrors}
						control={control}
						invalidMessage={localization.translate(
							'creator',
							'stepperOnboarding.steps.callSummariesTransfers.managerPhoneError'
						)}
						name="managerPhone"
						render={(phoneProps) => {
							phoneFieldOnChangeRef.current = phoneProps.onChange;

							return (
								<Box className={styles.managerPhoneInput}>
									<PhoneInput
										{...phoneProps}
										className="w-full [&>label]:hidden [&_input]:!rounded [&_input]:pr-9"
										description={undefined}
										error={undefined}
										inputProps={phoneInputProps}
										key={phoneInputVersion}
										onChange={handlePhoneInputChange}
									/>
									{hasManagerPhone && !isLocked && (
										<UnstyledButton
											aria-label={localization.translate(
												'creator',
												'stepperOnboarding.steps.callSummariesTransfers.clearManagerPhone'
											)}
											className="absolute right-2 top-5 z-10 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm text-[var(--color-primary-900)] transition-colors hover:bg-[var(--color-primary-100)]"
											data-testid="stepper-manager-phone-clear-button"
											onClick={handleClearPhone}
											onMouseDown={(event) => event.preventDefault()}
											type="button"
										>
											<X
												aria-hidden="true"
												className="h-3 w-3"
											/>
										</UnstyledButton>
									)}
									{phoneProps.error ? (
										<Text
											className="mt-1 text-sm leading-5 text-[#D92D20]"
											data-testid="stepper-manager-phone-error"
										>
											{phoneProps.error}
										</Text>
									) : undefined}
								</Box>
							);
						}}
						setError={setError}
					/>
				</Box>
			</Box>

			<Box className="mt-4">
				<StepperNextButton
					disabled={isPending || isLocked}
					loading={isPending}
					onClick={handleNextClick}
					testId="stepper-transfers-next-button"
				>
					{localization.translate(
						'creator',
						'stepperOnboarding.steps.callSummariesTransfers.next'
					)}
				</StepperNextButton>
			</Box>
		</Box>
	);
};

export default StepperTransfersStepContent;
